import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Lenis from "lenis";
import { ArrowLeft, ArrowRight, Download } from "lucide-react";

gsap.registerPlugin(ScrollTrigger);

const IMG = {
  ripple: "https://customer-assets-0z36b82j.emergentagent.net/job_aad-labs-export/artifacts/utht8u2s_ChatGPT%20Image%20Jul%2028%2C%202026%20at%2002_16_38%20PM.png",
  doubleWall: "https://customer-assets-0z36b82j.emergentagent.net/job_aad-labs-export/artifacts/5laajf1k_ChatGPT%20Image%20Jul%2028%2C%202026%20at%2002_18_10%20PM.png",
  embossed: "https://customer-assets-0z36b82j.emergentagent.net/job_aad-labs-export/artifacts/p4sezsjn_ChatGPT%20Image%20Jul%2028%2C%202026%20at%2002_20_26%20PM.png",
  singleWall: "https://customer-assets-0z36b82j.emergentagent.net/job_aad-labs-export/artifacts/g0f4d19w_ChatGPT%20Image%20Jul%2028%2C%202026%20at%2002_22_19%20PM.png",
  specialty: "https://customer-assets-0z36b82j.emergentagent.net/job_aad-labs-export/artifacts/yaadufhc_ChatGPT%20Image%20Jul%2028%2C%202026%20at%2002_24_38%20PM.png",
  lids: "https://customer-assets-0z36b82j.emergentagent.net/job_aad-labs-export/artifacts/zvn0t9kc_ChatGPT%20Image%20Jul%2028%2C%202026%20at%2002_26_11%20PM.png",
};

// Per-frame scroll length (in pixels) while pinned — snappy but still cinematic
const PIN_SCROLL = 550;

export default function EcoExperience() {
  const scopeRef = useRef(null);
  const progressBarRef = useRef(null);
  const cursorRef = useRef(null);
  const [loaded, setLoaded] = useState(false);

  // Preloader
  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 1400);
    return () => clearTimeout(t);
  }, []);

  // Lenis smooth scroll ✕ GSAP ticker
  useEffect(() => {
    if (!loaded) return;
    const lenis = new Lenis({
      duration: 1.3,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      wheelMultiplier: 0.95,
    });
    lenis.on("scroll", ScrollTrigger.update);
    const tickerCb = (time) => lenis.raf(time * 1000);
    gsap.ticker.add(tickerCb);
    gsap.ticker.lagSmoothing(0);
    ScrollTrigger.refresh();
    return () => {
      gsap.ticker.remove(tickerCb);
      lenis.destroy();
    };
  }, [loaded]);

  // Custom cursor
  useEffect(() => {
    if (!loaded) return;
    const cur = cursorRef.current;
    if (!cur) return;
    const move = (e) => {
      gsap.to(cur, { x: e.clientX, y: e.clientY, duration: 0.4, ease: "power3.out" });
    };
    const grow = () => gsap.to(cur, { scale: 2.4, duration: 0.3 });
    const shrink = () => gsap.to(cur, { scale: 1, duration: 0.3 });
    window.addEventListener("mousemove", move);
    const targets = document.querySelectorAll("[data-cursor='grow']");
    targets.forEach((el) => {
      el.addEventListener("mouseenter", grow);
      el.addEventListener("mouseleave", shrink);
    });
    return () => {
      window.removeEventListener("mousemove", move);
      targets.forEach((el) => {
        el.removeEventListener("mouseenter", grow);
        el.removeEventListener("mouseleave", shrink);
      });
    };
  }, [loaded]);

  // GSAP scroll orchestration — PIN-based, one frame at a time
  useEffect(() => {
    if (!loaded) return;
    const ctx = gsap.context(() => {
      const ease = "power3.out";

      // Global progress line (spans the entire scroll)
      gsap.to(progressBarRef.current, {
        height: "100%",
        ease: "none",
        scrollTrigger: {
          trigger: scopeRef.current,
          start: "top top",
          end: "bottom bottom",
          scrub: true,
        },
      });

      // Background luminance shift — warm forest-dark to deep brand tone
      gsap.to(scopeRef.current, {
        backgroundColor: "#152018",
        scrollTrigger: {
          trigger: scopeRef.current,
          start: "top top",
          end: "bottom bottom",
          scrub: true,
        },
      });

      // -------- FRAME 1: THE AWAKENING --------
      const tl1 = gsap.timeline({
        scrollTrigger: {
          trigger: ".frame-1",
          start: "top top",
          end: `+=${PIN_SCROLL}`,
          pin: true,
          scrub: 1.1,
        },
      });
      tl1
        .fromTo(
          ".f1-cup",
          { scale: 0.55, opacity: 0.7, filter: "brightness(0.75)" },
          { scale: 1, opacity: 1, filter: "brightness(1)", ease, duration: 1 },
          0
        )
        .fromTo(
          ".f1-word",
          {
            opacity: 0,
            y: (i) => (i === 2 ? 80 : -80),
            x: (i) => (i === 0 ? -140 : i === 2 ? 140 : 0),
            letterSpacing: "0.3em",
            scale: 1.15,
          },
          { opacity: 1, x: 0, y: 0, letterSpacing: "0.02em", scale: 1, stagger: 0.08, ease, duration: 1 },
          0
        )
        .fromTo(
          ".f1-headline",
          { opacity: 0, y: 40, letterSpacing: "0.25em" },
          { opacity: 1, y: 0, letterSpacing: "-0.01em", ease, duration: 0.8 },
          0.4
        );

      // -------- FRAME 2: THE LINEUP --------
      const tl2 = gsap.timeline({
        scrollTrigger: {
          trigger: ".frame-2",
          start: "top top",
          end: `+=${PIN_SCROLL}`,
          pin: true,
          scrub: 1.1,
        },
      });
      tl2
        .fromTo(
          ".f2-cup",
          { scale: 0.55, opacity: 0, y: 60 },
          {
            scale: 1,
            opacity: 1,
            y: (i) => (i === 0 ? -20 : i === 2 ? 20 : 0),
            stagger: 0.1,
            ease,
            duration: 1,
          },
          0
        )
        .fromTo(
          ".f2-text-left",
          { opacity: 0, x: -140, filter: "blur(8px)" },
          { opacity: 1, x: 0, filter: "blur(0px)", ease, duration: 0.9 },
          0.15
        )
        .fromTo(
          ".f2-text-right",
          { opacity: 0, x: 140, filter: "blur(8px)" },
          { opacity: 1, x: 0, filter: "blur(0px)", ease, duration: 0.9 },
          0.15
        )
        .fromTo(
          ".f2-rule",
          { scaleX: 0 },
          { scaleX: 1, ease: "power2.inOut", duration: 0.7 },
          0.4
        );

      // -------- FRAME 3: THE TEXTURE --------
      const tl3 = gsap.timeline({
        scrollTrigger: {
          trigger: ".frame-3",
          start: "top top",
          end: `+=${PIN_SCROLL}`,
          pin: true,
          scrub: 1.1,
        },
      });
      tl3
        .fromTo(
          ".f3-cup",
          { scale: 0.55, opacity: 0.55 },
          { scale: 1, opacity: 1, ease, duration: 1 },
          0
        )
        .fromTo(
          ".f3-detail",
          { opacity: 0, x: 100, y: 20 },
          { opacity: 1, x: 0, y: 0, stagger: 0.09, ease, duration: 0.85 },
          0.15
        );

      // -------- FRAME 4: THE SYSTEM --------
      const tl4 = gsap.timeline({
        scrollTrigger: {
          trigger: ".frame-4",
          start: "top top",
          end: `+=${PIN_SCROLL}`,
          pin: true,
          scrub: 1.1,
        },
      });
      tl4.fromTo(
        ".f4-title",
        { opacity: 0, letterSpacing: "0.35em", y: 30 },
        { opacity: 1, letterSpacing: "-0.01em", y: 0, ease, duration: 0.9 },
        0
      );
      const dirs = [
        { x: -180, y: -180 },
        { x: 180, y: -180 },
        { x: -180, y: 180 },
        { x: 180, y: 180 },
      ];
      gsap.utils.toArray(".f4-tile").forEach((tile, i) => {
        tl4.fromTo(
          tile,
          { opacity: 0, x: dirs[i].x, y: dirs[i].y, scale: 0.65, filter: "blur(10px)" },
          { opacity: 1, x: 0, y: 0, scale: 1, filter: "blur(0px)", ease, duration: 0.9 },
          0.15
        );
      });
      tl4.fromTo(
        ".f4-blueprint span",
        { opacity: 0, y: 15 },
        { opacity: 1, y: 0, stagger: 0.06, ease, duration: 0.6 },
        0.75
      );

      // -------- FRAME 5: THE CLIMAX --------
      const tl5 = gsap.timeline({
        scrollTrigger: {
          trigger: ".frame-5",
          start: "top top",
          end: `+=${PIN_SCROLL + 200}`,
          pin: true,
          scrub: 1.1,
        },
      });
      tl5
        .fromTo(
          ".f5-hero",
          { scale: 0.55, opacity: 0.35, filter: "brightness(0.45)" },
          { scale: 1, opacity: 1, filter: "brightness(1)", ease, duration: 1 },
          0
        )
        .fromTo(
          ".f5-manifesto > *",
          { opacity: 0, y: 30 },
          { opacity: 1, y: 0, stagger: 0.08, ease, duration: 0.8 },
          0.3
        )
        .fromTo(
          ".f5-cta",
          { opacity: 0, y: 40 },
          { opacity: 1, y: 0, stagger: 0.12, ease, duration: 0.8 },
          0.65
        );
    }, scopeRef);
    return () => ctx.revert();
  }, [loaded]);

  return (
    <>
      {/* Preloader */}
      <div
        className={`fixed inset-0 z-[80] flex items-center justify-center bg-[#1E2A22] transition-opacity duration-1000 ${
          loaded ? "opacity-0 pointer-events-none" : "opacity-100"
        }`}
        data-testid="eco-experience-preloader"
      >
        <div className="text-center">
          <div className="w-14 h-14 rounded-full border border-[#C9A227]/40 border-t-[#C9A227] animate-spin mx-auto" />
          <div className="mt-6 text-[10px] tracking-[0.4em] uppercase text-[#C9A227]/85">
            Assembling your experience
          </div>
        </div>
      </div>

      {/* Custom cursor */}
      <div
        ref={cursorRef}
        className="pointer-events-none fixed top-0 left-0 z-[70] w-6 h-6 rounded-full border border-[#C9A227]/80 mix-blend-difference hidden md:block"
        style={{ transform: "translate(-50%, -50%)" }}
      />

      {/* Return chrome */}
      <Link
        to="/products/eco-packaging"
        data-testid="eco-experience-back"
        data-cursor="grow"
        className="fixed top-6 left-6 z-[60] flex items-center gap-2 px-4 py-2 text-[10px] tracking-[0.35em] uppercase text-[#C9A227] border border-[#C9A227]/40 rounded-full backdrop-blur-md bg-[#152018]/60 hover:border-[#C9A227] hover:bg-[#152018]/85 transition"
      >
        <ArrowLeft className="w-3 h-3" />
        Overview
      </Link>

      {/* Progress line */}
      <div className="fixed top-0 right-6 h-full w-px z-[50] pointer-events-none">
        <div className="relative h-full w-full bg-[#C9A227]/15">
          <div ref={progressBarRef} className="absolute top-0 left-0 w-full bg-[#C9A227]" style={{ height: 0 }} />
        </div>
      </div>

      {/* Main scroll canvas */}
      <div
        ref={scopeRef}
        data-testid="eco-experience-scope"
        className="relative bg-[#1E2A22] text-white overflow-hidden"
        style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif" }}
      >
        {/* Ambient warm radial + soft grain */}
        <div
          className="pointer-events-none fixed inset-0 z-0"
          style={{
            background:
              "radial-gradient(ellipse at 50% 40%, rgba(201,162,39,0.10) 0%, rgba(27,94,32,0.08) 40%, transparent 70%)",
          }}
        />
        <div
          className="pointer-events-none fixed inset-0 z-0 opacity-[0.04]"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='140' height='140'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='1'/></svg>\")",
          }}
        />

        {/* ============ FRAME 1: THE AWAKENING ============ */}
        <section className="frame-1 relative h-screen w-full overflow-hidden">
          {/* Top strip — three words spread horizontally, well above the cup */}
          <div className="absolute top-[10%] left-0 right-0 flex justify-center items-center gap-[8vw] px-8 pointer-events-none z-20">
            <div
              className="f1-word text-[#C9A227]/85"
              style={{
                fontFamily: "'Playfair Display', 'Didot', serif",
                fontSize: "clamp(1.4rem, 2.6vw, 2.6rem)",
                fontWeight: 300,
                whiteSpace: "nowrap",
              }}
            >
              Compostable.
            </div>
            <div
              className="f1-word text-white/90"
              style={{
                fontFamily: "'Playfair Display', 'Didot', serif",
                fontSize: "clamp(1.5rem, 2.8vw, 2.8rem)",
                fontWeight: 300,
                whiteSpace: "nowrap",
              }}
            >
              Premium.
            </div>
            <div
              className="f1-word text-[#C9A227]/75"
              style={{
                fontFamily: "'Playfair Display', 'Didot', serif",
                fontSize: "clamp(1.4rem, 2.6vw, 2.6rem)",
                fontWeight: 300,
                whiteSpace: "nowrap",
              }}
            >
              Global.
            </div>
          </div>

          {/* Center — the hero cup, big and dominant */}
          <div className="absolute inset-0 flex items-center justify-center z-10">
            <div className="f1-cup relative w-[62vw] max-w-[820px] aspect-[4/3]" data-cursor="grow">
              <div
                className="absolute inset-0 rounded-3xl"
                style={{
                  boxShadow:
                    "0 0 240px 80px rgba(201,162,39,0.18), inset 0 0 140px 60px rgba(0,0,0,0.35)",
                }}
              />
              <img
                src={IMG.ripple}
                alt="Kraft ripple wall cup"
                className="relative w-full h-full object-contain drop-shadow-[0_30px_120px_rgba(201,162,39,0.3)]"
                loading="eager"
              />
            </div>
          </div>

          {/* Bottom — locked headline */}
          <div
            className="f1-headline absolute left-1/2 -translate-x-1/2 bottom-[6%] text-center text-white px-8 w-full max-w-6xl z-20"
            style={{
              fontFamily: "'Playfair Display', 'Didot', serif",
              fontWeight: 300,
              fontSize: "clamp(2rem, 5.2vw, 5.2rem)",
              lineHeight: 1,
            }}
          >
            Engineered for <em className="italic text-[#C9A227] font-light">Excellence.</em>
          </div>

          <div className="absolute bottom-6 left-6 text-[10px] tracking-[0.4em] uppercase text-white/50 z-20">
            I · The Awakening
          </div>
        </section>

        {/* ============ FRAME 2: THE LINEUP ============ */}
        <section className="frame-2 relative h-screen w-full flex items-center justify-center overflow-hidden">
          <div className="max-w-[1500px] w-full px-6 md:px-10 grid grid-cols-12 gap-4 md:gap-8 items-center">
            <div
              className="f2-text-left col-span-3 hidden md:block"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              <div className="text-[10px] tracking-[0.4em] uppercase text-[#C9A227] mb-3">01 / Ripple</div>
              <div className="text-3xl lg:text-5xl font-light leading-[1.02]">
                Ripple Wall<br />
                <em className="italic text-[#C9A227]/95">Collection.</em>
              </div>
              <div
                className="mt-5 text-sm text-white/70 leading-relaxed"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                8 oz – 20 oz · Kraft &amp; Matte Black.<br />
                Corrugated exterior. Tactile hand-feel by design.
              </div>
            </div>

            <div className="col-span-12 md:col-span-6 grid grid-cols-3 gap-3 md:gap-5">
              <div className="f2-cup aspect-[3/4] flex items-center">
                <img src={IMG.ripple} alt="Ripple" className="w-full h-full object-contain drop-shadow-[0_20px_60px_rgba(0,0,0,0.5)]" />
              </div>
              <div className="f2-cup aspect-[3/4] flex items-center">
                <img src={IMG.doubleWall} alt="Double wall" className="w-full h-full object-contain drop-shadow-[0_20px_60px_rgba(0,0,0,0.5)]" />
              </div>
              <div className="f2-cup aspect-[3/4] flex items-center">
                <img src={IMG.embossed} alt="Embossed" className="w-full h-full object-contain drop-shadow-[0_20px_60px_rgba(0,0,0,0.5)]" />
              </div>
            </div>

            <div
              className="f2-text-right col-span-3 hidden md:block text-right"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              <div className="text-[10px] tracking-[0.4em] uppercase text-[#C9A227] mb-3">02 / Double Wall</div>
              <div className="text-3xl lg:text-5xl font-light leading-[1.02]">
                Double Wall<br />
                <em className="italic text-[#C9A227]/95">Series.</em>
              </div>
              <div
                className="mt-5 text-sm text-white/70 leading-relaxed"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                7 oz – 20 oz · Seamless construction.<br />
                Maximum brand real-estate. Sleeve-free thermal comfort.
              </div>
            </div>

            <div
              className="f2-rule col-span-12 h-px bg-gradient-to-r from-transparent via-[#C9A227]/60 to-transparent mt-8"
              style={{ transformOrigin: "center" }}
            />
          </div>

          <div className="absolute bottom-6 left-6 text-[10px] tracking-[0.4em] uppercase text-white/50">
            II · The Lineup
          </div>
        </section>

        {/* ============ FRAME 3: THE TEXTURE ============ */}
        <section className="frame-3 relative h-screen w-full flex items-center justify-center overflow-hidden">
          <div className="max-w-[1500px] w-full px-6 md:px-10 grid grid-cols-12 gap-10 md:gap-14 items-center">
            <div className="f3-cup col-span-12 md:col-span-7 relative aspect-[4/3]">
              <div
                className="absolute inset-0"
                style={{
                  background:
                    "radial-gradient(ellipse at center, rgba(201,162,39,0.22) 0%, transparent 62%)",
                }}
              />
              <img
                src={IMG.embossed}
                alt="Embossed collection macro"
                className="relative w-full h-full object-contain drop-shadow-[0_40px_120px_rgba(201,162,39,0.28)]"
              />
            </div>

            <div className="col-span-12 md:col-span-5 space-y-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              <div className="f3-detail">
                <div className="text-[10px] tracking-[0.4em] uppercase text-[#C9A227] mb-3">
                  Deep-Relief Embossing
                </div>
                <div className="text-5xl md:text-6xl font-light leading-[1.02]">
                  Tactile.<br />
                  Distinctive.<br />
                  <em className="italic text-[#C9A227]/95">Unmistakable.</em>
                </div>
              </div>
              <div className="f3-detail h-px w-24 bg-[#C9A227]/50" />
              <div
                className="f3-detail text-white/75 text-base leading-relaxed"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Standard &amp; deep-bottom vending formats. Custom relief plates developed to your specification — from monograms to full botanical motifs.
              </div>
              <div
                className="f3-detail flex flex-wrap gap-x-6 gap-y-2 text-[10px] tracking-[0.35em] uppercase text-white/60"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                <span>· Standard 8/12/16/20 oz</span>
                <span>· Vending 12 oz</span>
              </div>
            </div>
          </div>

          <div className="absolute bottom-6 left-6 text-[10px] tracking-[0.4em] uppercase text-white/50">
            III · The Texture
          </div>
        </section>

        {/* ============ FRAME 4: THE SYSTEM ============ */}
        <section className="frame-4 relative h-screen w-full flex flex-col items-center justify-center overflow-hidden py-12 md:py-16">
          <div
            className="f4-title text-center px-8 mb-5 md:mb-7"
            style={{
              fontFamily: "'Playfair Display', serif",
              fontWeight: 300,
              fontSize: "clamp(2rem, 4.6vw, 4.6rem)",
              lineHeight: 1,
            }}
          >
            Complete Serving <em className="italic text-[#C9A227]">Systems.</em>
          </div>

          <div className="max-w-[1250px] w-full px-6 md:px-10 grid grid-cols-2 gap-4 md:gap-7 flex-1 max-h-[64vh]">
            {[
              { img: IMG.singleWall, label: "Single Wall · Hot & Cold" },
              { img: IMG.specialty, label: "Specialty · Pint & Party" },
              { img: IMG.embossed, label: "Embossed · Premium" },
              { img: IMG.lids, label: "Lids · Straws · Accessories" },
            ].map((q, i) => (
              <div
                key={i}
                className="f4-tile relative aspect-[4/3] bg-[#28352C] rounded-lg overflow-hidden border border-[#C9A227]/15 hover:border-[#C9A227]/40 transition"
                data-cursor="grow"
              >
                <img src={q.img} alt={q.label} className="w-full h-full object-contain p-3" />
                <div className="absolute bottom-2 left-3 text-[10px] md:text-[11px] tracking-[0.28em] uppercase text-[#C9A227]/90">
                  {q.label}
                </div>
              </div>
            ))}
          </div>

          <div
            className="f4-blueprint mt-6 md:mt-8 flex flex-wrap justify-center gap-x-6 gap-y-2 text-[10px] tracking-[0.28em] uppercase text-white/60 px-8 max-w-4xl"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            <span>Compostable Certified</span>
            <span className="text-[#C9A227]/70">·</span>
            <span>Food-Grade</span>
            <span className="text-[#C9A227]/70">·</span>
            <span>Export Carton Optimized</span>
            <span className="text-[#C9A227]/70">·</span>
            <span>40'HC Logistics Ready</span>
          </div>

          <div className="absolute bottom-6 left-6 text-[10px] tracking-[0.4em] uppercase text-white/50">
            IV · The System
          </div>
        </section>

        {/* ============ FRAME 5: THE CLIMAX ============ */}
        <section className="frame-5 relative h-screen w-full flex flex-col items-center justify-center overflow-hidden py-10" style={{ background: "linear-gradient(180deg, #152018 0%, #0F1A13 100%)" }}>
          <div className="max-w-[1400px] w-full px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-14 items-center">
            <div className="f5-hero relative col-span-12 md:col-span-7 aspect-[4/3] max-h-[68vh] mx-auto w-full">
              <div
                className="absolute inset-0"
                style={{
                  background:
                    "radial-gradient(ellipse at center, rgba(201,162,39,0.25) 0%, transparent 60%)",
                }}
              />
              <img
                src={IMG.doubleWall}
                alt="Custom-branded compostable cup"
                className="relative w-full h-full object-contain drop-shadow-[0_50px_140px_rgba(201,162,39,0.35)]"
              />
            </div>

            <div className="col-span-12 md:col-span-5">
              <div className="f5-manifesto">
                <div className="text-[10px] tracking-[0.4em] uppercase text-[#C9A227] mb-4">
                  V · The Manifesto
                </div>
                <div
                  className="text-4xl md:text-5xl lg:text-6xl font-light leading-[1.02]"
                  style={{ fontFamily: "'Playfair Display', 'Didot', serif" }}
                >
                  Your Brand.<br />
                  <em className="italic text-[#C9A227]">Our Standard.</em>
                </div>
                <div className="h-px w-24 bg-[#C9A227]/60 mt-6" />
                <div
                  className="mt-5 text-base text-white/80 leading-relaxed max-w-md"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  From precision plate development to global container logistics, we transform sustainable packaging into a competitive advantage. Every cup is a statement. Every order is a partnership.
                </div>

                <div className="mt-8 flex flex-wrap gap-3">
                  <Link
                    to="/products/eco-packaging#sample-kit"
                    data-testid="eco-experience-cta-primary"
                    data-cursor="grow"
                    className="f5-cta inline-flex items-center gap-2 px-6 py-3.5 bg-[#C9A227] text-[#152018] text-[11px] tracking-[0.32em] uppercase font-semibold hover:bg-[#D9B237] transition"
                  >
                    Request Volume Pricing
                    <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                  <Link
                    to="/contact"
                    data-testid="eco-experience-cta-secondary"
                    data-cursor="grow"
                    className="f5-cta inline-flex items-center gap-2 px-6 py-3.5 border border-[#C9A227]/70 text-[#C9A227] text-[11px] tracking-[0.32em] uppercase font-semibold hover:bg-[#C9A227]/10 transition"
                  >
                    <Download className="w-3.5 h-3.5" />
                    Technical Specs
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <div className="absolute bottom-6 left-6 text-[10px] tracking-[0.4em] uppercase text-white/50">
            V · The Climax
          </div>
          <div className="absolute bottom-6 right-14 text-[10px] tracking-[0.4em] uppercase text-[#C9A227]/70 hidden md:block">
            Aad Labs · Est. 2026
          </div>
        </section>
      </div>
    </>
  );
}
